import type { ReactNode, Ref } from 'react'

/**
 * Hairline rules with even vertical padding, and no padding at the ends. Applied
 * to the article AND to each zone, so a zone holding several sections spaces
 * them exactly the way the zones themselves are spaced.
 *
 * Counted over the children that show. Sections hide themselves with
 * `empty:hidden`, and `divide-y` / `:last-child` still counted a hidden one —
 * a Photos wrapper with nothing in it left the section above it a rule and a
 * gap of its own, so the pane drew two rules with nothing between them
 * (detail pane standard, 2026-10-19). A rule goes above every shown child
 * that has a shown child before it; padding stops at the first and last shown.
 */
const RHYTHM =
  '[&>:not(:empty)~:not(:empty)]:border-t [&>*]:border-neutral-200 [&>:not(:empty)]:py-4 ' +
  '[&>:not(:empty):not(:not(:empty)~*)]:pt-0 [&>:not(:empty):not(:has(~:not(:empty)))]:pb-0'

export interface PanelShellProps {
  identity: ReactNode
  act?: ReactNode
  classify?: ReactNode
  details?: ReactNode
  related?: ReactNode
  footer?: ReactNode
  /** Forwarded to the <article> — the task and event panels use it as the file drop zone. */
  innerRef?: Ref<HTMLElement>
  /** Rendered outside the divided flow: overlays, drawers, portaled chrome. */
  children?: ReactNode
}

/**
 * The detail panel's chrome and zone order, in one place.
 *
 * Panels used to style themselves, and drifted into three rhythms in one app:
 * task and event on hairline dividers with even padding, routine on p-5 and meal
 * on p-6 with none. A panel now supplies zones; the order and the spacing are
 * not its business.
 *
 * A zone that renders nothing draws nothing — no empty wrapper — so `divide-y`
 * never lays down a hairline with nothing on either side of it. Pass `undefined`
 * for an unused zone, not an empty fragment: a fragment is still a zone.
 */
export function PanelShell({
  identity,
  act,
  classify,
  details,
  related,
  footer,
  innerRef,
  children,
}: PanelShellProps) {
  const zones: ReactNode[] = [identity, act, classify, details, related, footer]

  return (
    <article
      ref={innerRef}
      className={`panel-shell relative w-full max-w-md rounded-lg border border-neutral-200 bg-bg-elevated px-4 py-4 md:px-5 md:py-5 ${RHYTHM}`}
    >

      {zones.map((zone, i) =>
        // The rhythm repeats INSIDE a zone: `details` holds many sections, and
        // without it Phone and Notes butt together with no rule between them
        // while zones a single section wide keep their breathing room.
        zone == null || zone === false ? null : (
          // `empty:hidden` is load-bearing, not defensive. A zone is usually a
          // fragment of sections that each decide for themselves whether to
          // render — `related` on a task with no project and no suggestions is a
          // truthy fragment that produces nothing — and a wrapper with no
          // children still draws a divider and a chunk of padding.
          // `relative` stays: it keeps each zone at the default z-index rather
          // than making it a stacking context, which would trap a popover
          // opened from inside a zone.
          // Identity is one statement — kind, title, the facts under it — so it
          // carries no inner rules (detail pane standard, 2026-10-19: a rule
          // under the title, another under the goal line and a third under the
          // date read as three unrelated blocks).
          <div key={i} className={`relative ${i === 0 ? '' : RHYTHM} empty:hidden`}>
            {zone}
          </div>
        ),
      )}
      {children}
    </article>
  )
}
